import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        const raw = localStorage.getItem("nexus_user");
        return raw ? JSON.parse(raw) : null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("nexus_token");
        if (!token) {
            setLoading(false);
            return;
        }
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                localStorage.setItem("nexus_user", JSON.stringify(r.data));
            })
            .catch(() => {
                setUser(null);
                localStorage.removeItem("nexus_token");
                localStorage.removeItem("nexus_user");
            })
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally run only on mount

    const login = async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        localStorage.setItem("nexus_token", r.data.token);
        localStorage.setItem("nexus_user", JSON.stringify(r.data.user));
        setUser(r.data.user);
        return r.data.user;
    };

    const signup = async (email, password, display_name, referral_code) => {
        const payload = { email, password, display_name };
        if (referral_code) payload.referral_code = referral_code;
        const r = await api.post("/auth/signup", payload);
        localStorage.setItem("nexus_token", r.data.token);
        localStorage.setItem("nexus_user", JSON.stringify(r.data.user));
        setUser(r.data.user);
        return r.data.user;
    };

    const refresh = async () => {
        const r = await api.get("/auth/me");
        setUser(r.data);
        localStorage.setItem("nexus_user", JSON.stringify(r.data));
        return r.data;
    };

    const logout = () => {
        localStorage.removeItem("nexus_token");
        localStorage.removeItem("nexus_user");
        setUser(null);
    };

    // Memoize the context value so consumers only re-render when user/loading
    // actually change, not on every AuthProvider render. login/signup/logout/refresh
    // are stable closures over setUser (a stable setState ref), so safe to omit.
    const value = useMemo(
        () => ({ user, loading, login, signup, logout, refresh }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user, loading]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
